// Recharts implementation of InteractiveChart
// Timestamp: 2025-12-25T09:48:00.000Z

import React, { useState, useMemo } from 'react';
import {
  LineChart,
  BarChart,
  PieChart,
  RadarChart,
  ScatterChart,
  Line,
  Bar,
  Pie,
  Cell,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { useAnalytics } from './AdvancedAnalyticsProvider';
import { transformChartDataOptimized } from '../../utils/chartDataTransformer';
import { ChartDataPoint } from '../../types';
import { HelpButton } from '../Help/HelpButton';

interface RechartsChartProps {
  type: 'line' | 'bar' | 'pie' | 'radar' | 'scatter';
  title: string;
  height?: number;
  onPointClick?: (point: ChartDataPoint, data: any) => void;
}

// Format values for display
const formatValue = (value: number, type: string = 'number'): string => {
  switch (type) {
    case 'currency':
      return `GH₵${value.toLocaleString()}`;
    case 'number':
      return value.toLocaleString();
    default:
      return value.toString();
  }
};

/**
 * Recharts implementation of the interactive chart
 */
export const RechartsInteractiveChart: React.FC<RechartsChartProps> = ({
  type,
  title,
  height = 300,
  onPointClick
}) => {
  const { chartData, setDrillDownData } = useAnalytics();
  const [error, setError] = useState<string | null>(null);

  // Transform chart data for Recharts
  const rechartsData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { data: [], config: null };
    }

    try {
      const result = transformChartDataOptimized(
        chartData,
        type,
        {
          batchSize: 1000,
          enableValidation: false
        }
      );

      return {
        data: result.config.data,
        config: result.config
      };
    } catch (err) {
      console.error('Data transformation failed', err);
      setError('Failed to transform chart data');
      return { data: [], config: null };
    }
  }, [chartData, type]);

  // Handle point clicks
  const handlePointClick = (data: any, index: number) => {
    if (onPointClick && chartData && chartData[index]) {
      onPointClick(chartData[index], data);
    }

    // Set drill-down data for the clicked point
    if (chartData && chartData[index]) {
      setDrillDownData({
        type: 'timePoint',
        data: chartData[index],
        index
      });
    }
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-dark-bg-secondary p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 dark:text-white">{label}</p>
          {payload.map((entry: any, idx: number) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {entry.name || entry.dataKey}: {formatValue(entry.value, entry.name?.toLowerCase().includes('revenue') ? 'currency' : 'number')}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom legend component
  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex justify-center flex-wrap gap-4 mt-2">
        {payload?.map((entry: any, index: number) => (
          <div key={`item-${index}`} className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render error state
  if (error) {
    return (
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
        <div className="w-full flex items-center justify-center" style={{ height: `${height}px` }}>
          <p className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6">
      <div className="flex items-center justify-center gap-2 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <HelpButton guideId="dashboard/chart-usage" variant="icon" size="sm" />
      </div>

      <div className="w-full" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <>
            {type === 'line' && (
              <LineChart data={rechartsData.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(value) => formatValue(value, 'currency')} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} onClick={(props: any) => handlePointClick(props.payload, props.index)} />
                <Line type="monotone" dataKey="members" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="5 5" />
              </LineChart>
            )}

            {type === 'bar' && (
              <BarChart data={rechartsData.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(value) => formatValue(value, 'number')} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Bar dataKey="contributions" fill="#8b5cf6" radius={[4, 4, 0, 0]} onClick={(props: any) => handlePointClick(props.payload, props.index)} />
                <Bar dataKey="benefits" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="events" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            )}

            {type === 'pie' && (
              <PieChart>
                <Pie
                  data={rechartsData.data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={(props: any) => handlePointClick(props.payload, props.index)}
                >
                  {rechartsData.data.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color || `hsl(${index * 60}, 70%, 50%)`} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            )}

            {type === 'radar' && (
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={rechartsData.data}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" stroke="#6b7280" fontSize={12} />
                <PolarRadiusAxis angle={30} domain={[0, 'dataMax']} stroke="#6b7280" />
                <Radar name="Metrics" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                <Legend content={<CustomLegend />} />
                <Tooltip content={<CustomTooltip />} />
              </RadarChart>
            )}

            {type === 'scatter' && (
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="members" stroke="#6b7280" fontSize={12} label={{ value: 'Members', position: 'insideBottomRight', offset: 0 }} />
                <YAxis dataKey="revenue" stroke="#6b7280" fontSize={12} tickFormatter={(value) => formatValue(value, 'currency')} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Scatter name="Revenue vs Members" data={rechartsData.data} fill="#3b82f6" onClick={(props: any) => handlePointClick(props.payload, props.index)} />
              </ScatterChart>
            )}
          </>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RechartsInteractiveChart;